/**
 * Claude API Client for Semantic Extraction
 *
 * Uses Anthropic's Claude API to extract structured entities from unstructured
 * business documents using LLM-based semantic inference.
 */

export interface ClaudeConfig {
  apiKey: string
  model?: string
  maxTokens?: number
  temperature?: number  // Controls randomness: 0 = deterministic, 1 = creative
  seed?: string         // For reproducible outputs (requires temperature < 1)
}

export interface ImageContent {
  type: 'base64' | 'url'
  data: string
  mediaType: 'image/png' | 'image/jpeg' | 'image/gif' | 'image/webp'
}

export interface ClaudeMessage {
  role: 'user' | 'assistant'
  content: string
}

export interface ClaudeResponse {
  content: Array<{ type: 'text'; text: string }>
  usage: {
    input_tokens: number
    output_tokens: number
  }
}

// Streaming event types
interface StreamDelta {
  type: 'content_block_delta'
  delta: { type: 'text_delta'; text: string }
}

interface StreamStart {
  type: 'content_block_start'
}

interface StreamStop {
  type: 'message_stop'
}

type StreamEvent = StreamDelta | StreamStart | StreamStop | { type: string }

const DEFAULT_MODEL = 'claude-sonnet-4-20250514'
const DEFAULT_MAX_TOKENS = 8192 // Max supported by Haiku; Sonnet callers can pass explicit maxTokens if needed

/**
 * Call Claude API for semantic extraction (non-streaming)
 */
export async function callClaude(
  config: ClaudeConfig,
  systemPrompt: string,
  userMessage: string
): Promise<string> {
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config.apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: config.model || DEFAULT_MODEL,
      max_tokens: config.maxTokens || DEFAULT_MAX_TOKENS,
      temperature: config.temperature ?? 1.0,
      ...(config.seed && { seed: config.seed }),
      system: systemPrompt,
      messages: [
        {
          role: 'user',
          content: userMessage,
        },
      ],
    }),
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`Claude API error: ${response.status} - ${error}`)
  }

  const data = (await response.json()) as ClaudeResponse
  // Concatenate all text blocks (Claude can return multiple content blocks for long responses)
  return data.content
    .filter(block => block.type === 'text')
    .map(block => block.text)
    .join('')
}

/**
 * Call Claude API with image support for vision tasks
 */
export async function callClaudeWithImages(
  config: ClaudeConfig,
  systemPrompt: string,
  userMessage: string,
  images: ImageContent[]
): Promise<string> {
  // Build content array with images and text
  const content: Array<
    | { type: 'image'; source: { type: 'base64'; media_type: string; data: string } }
    | { type: 'image'; source: { type: 'url'; url: string } }
    | { type: 'text'; text: string }
  > = []

  // Add images first
  for (const img of images) {
    if (img.type === 'base64') {
      content.push({
        type: 'image',
        source: {
          type: 'base64',
          media_type: img.mediaType,
          data: img.data
        }
      })
    } else {
      content.push({
        type: 'image',
        source: {
          type: 'url',
          url: img.data
        }
      })
    }
  }

  // Add text message
  content.push({ type: 'text', text: userMessage })

  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config.apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: config.model || DEFAULT_MODEL,
      max_tokens: config.maxTokens || DEFAULT_MAX_TOKENS,
      temperature: config.temperature ?? 0, // Use 0 for consistent classification
      ...(config.seed && { seed: config.seed }),
      system: systemPrompt,
      messages: [{ role: 'user', content }],
    }),
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`Claude API error: ${response.status} - ${error}`)
  }

  const data = (await response.json()) as ClaudeResponse
  // Concatenate all text blocks (Claude can return multiple content blocks for long responses)
  return data.content
    .filter(block => block.type === 'text')
    .map(block => block.text)
    .join('')
}

/**
 * Call Claude API with streaming - calls onChunk for each text delta
 */
export async function callClaudeStreaming(
  config: ClaudeConfig,
  systemPrompt: string,
  userMessage: string,
  onChunk: (text: string) => void
): Promise<string> {
  const response = await fetch('https://api.anthropic.com/v1/messages', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': config.apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: config.model || DEFAULT_MODEL,
      max_tokens: config.maxTokens || DEFAULT_MAX_TOKENS,
      temperature: config.temperature ?? 1.0,
      ...(config.seed && { seed: config.seed }),
      system: systemPrompt,
      stream: true,
      messages: [
        {
          role: 'user',
          content: userMessage,
        },
      ],
    }),
  })

  if (!response.ok) {
    const error = await response.text()
    throw new Error(`Claude API error: ${response.status} - ${error}`)
  }

  const reader = response.body?.getReader()
  if (!reader) {
    throw new Error('No response body')
  }

  const decoder = new TextDecoder()
  let fullText = ''
  let buffer = ''

  while (true) {
    const { done, value } = await reader.read()
    if (done) break

    buffer += decoder.decode(value, { stream: true })

    // Process complete SSE events from buffer
    const lines = buffer.split('\n')
    buffer = lines.pop() || '' // Keep incomplete line in buffer

    for (const line of lines) {
      if (line.startsWith('data: ')) {
        const data = line.slice(6).trim()
        if (data === '[DONE]') continue

        try {
          const event = JSON.parse(data) as StreamEvent
          if (event.type === 'content_block_delta' && 'delta' in event) {
            const delta = event as StreamDelta
            if (delta.delta.type === 'text_delta') {
              fullText += delta.delta.text
              onChunk(delta.delta.text)
            }
          }
        } catch {
          // Skip malformed JSON
        }
      }
    }
  }

  return fullText
}

/**
 * Clean JSON response from Claude (remove markdown code blocks if present)
 */
export function cleanJsonResponse(response: string): string {
  // Remove markdown code blocks
  let cleaned = response
    .replace(/```json\s*/gi, '')
    .replace(/```\s*/g, '')
    .trim()

  // Find the JSON object boundaries
  const startIndex = cleaned.indexOf('{')
  const endIndex = cleaned.lastIndexOf('}')

  if (startIndex !== -1 && endIndex !== -1 && endIndex > startIndex) {
    cleaned = cleaned.slice(startIndex, endIndex + 1)
  }

  return cleaned
}

/**
 * Attempt to repair truncated JSON (e.g., when response is cut off)
 * Handles cases where a JSON array was truncated mid-object
 */
function repairTruncatedJson(json: string): string {
  // If it parses already, return as-is
  try {
    JSON.parse(json)
    return json
  } catch {
    // Continue with repair
  }

  let repaired = json.trim()

  // Try to find the facets array and close it properly
  // Look for truncated patterns like: ..."field": "value (no closing quote/brace)

  // Remove incomplete trailing content after the last complete object in an array
  // Pattern: Look for the last complete }, in a facets array
  const facetsMatch = repaired.match(/"facets"\s*:\s*\[/)
  if (facetsMatch) {
    // Find position of facets array start
    const arrayStart = repaired.indexOf('[', repaired.indexOf('"facets"'))

    // Find all complete objects (ending with },) in the array
    let depth = 0
    let lastCompleteObjectEnd = -1
    let inString = false
    let escapeNext = false

    for (let i = arrayStart + 1; i < repaired.length; i++) {
      const char = repaired[i]

      if (escapeNext) {
        escapeNext = false
        continue
      }

      if (char === '\\') {
        escapeNext = true
        continue
      }

      if (char === '"' && !escapeNext) {
        inString = !inString
        continue
      }

      if (inString) continue

      if (char === '{') depth++
      else if (char === '}') {
        depth--
        if (depth === 0) {
          // Found end of a complete object
          // Check if followed by comma (more items) or ] (end of array)
          const afterObj = repaired.slice(i + 1).trim()
          if (afterObj.startsWith(',') || afterObj.startsWith(']')) {
            lastCompleteObjectEnd = i
          }
        }
      }
    }

    // If we found at least one complete object, truncate after it
    if (lastCompleteObjectEnd > 0) {
      repaired = repaired.slice(0, lastCompleteObjectEnd + 1)
      // Close the array and object
      repaired = repaired.trimEnd()
      if (repaired.endsWith(',')) {
        repaired = repaired.slice(0, -1)
      }
      repaired += ']}'

      // Verify it parses
      try {
        JSON.parse(repaired)
        console.log('[claude] Repaired truncated JSON successfully')
        return repaired
      } catch {
        // Repair failed, continue with other strategies
      }
    }
  }

  // Simple fallback: try closing brackets
  let attempts = [
    repaired + ']}',
    repaired + '}]}',
    repaired + '"}]}',
    repaired + '"}]}'
  ]

  for (const attempt of attempts) {
    try {
      JSON.parse(attempt)
      console.log('[claude] Repaired truncated JSON with simple bracket closure')
      return attempt
    } catch {
      continue
    }
  }

  // Return original if repair failed
  return json
}

/**
 * Parse JSON response safely, with truncation repair
 */
export function parseJsonResponse<T>(response: string): T | null {
  try {
    const cleaned = cleanJsonResponse(response)
    return JSON.parse(cleaned) as T
  } catch (error) {
    // Try to repair truncated JSON
    console.warn('[claude] Initial JSON parse failed, attempting repair...', error)
    try {
      const cleaned = cleanJsonResponse(response)
      const repaired = repairTruncatedJson(cleaned)
      const result = JSON.parse(repaired) as T
      console.log('[claude] JSON repair successful')
      return result
    } catch (repairError) {
      console.error('[claude] Failed to parse Claude response as JSON:', error)
      console.error('[claude] Repair also failed:', repairError)
      console.error('[claude] Raw response (first 1000 chars):', response.slice(0, 1000))
      return null
    }
  }
}
